import { useState, useEffect } from "react";
import PlatformLayout from "../components/PlatformLayout";
import { CHINA_PROVINCES, getTemplateName } from "@/constants/provinces";

interface Site {
  id: string;
  name: string;
  subdomain: string;
  agent: string;
  agent_id?: string;
  owner_id?: string;
  owner_name?: string;
  status: "active" | "stopped" | "expired";
  platform_disabled?: number;
  domains: string[];
  created_at: string;
  expire_at: string;
  province?: string;
  template_id?: string;
}

const statusMap = {
  active: { label: "运行中", bg: "bg-green-50", color: "text-green-600" },
  stopped: { label: "已停止", bg: "bg-gray-100", color: "text-gray-600" },
  expired: { label: "已到期", bg: "bg-red-50", color: "text-red-600" },
};

export default function PlatformSitesPage() {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [provinceFilter, setProvinceFilter] = useState<string>("all");
  const [toast, setToast] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const siteId = localStorage.getItem('current_site_id') || '';
      const res = await fetch('/api/platform/sites?page=1&limit=100', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Site-Id': siteId,
        },
      });
      const data = await res.json();
      if (data.code === 0 && data.data?.list) {
        setSites(data.data.list);
      }
    } catch (e) {
      console.error('获取站点列表失败', e);
    } finally {
      setLoading(false);
    }
  };

  const updateSiteStatus = async (siteId: string, action: 'enable' | 'disable') => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`/api/platform/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ action }),
      });
      const data = await res.json();
      if (data.code === 0) {
        showToast(data.message || '操作成功');
        fetchSites();
      } else {
        showToast(data.message || '操作失败');
      }
    } catch (e) {
      showToast('操作失败，请检查网络');
    }
  };

  const filteredSites = sites.filter((site) => {
    const matchesSearch = site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      site.subdomain.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || site.status === statusFilter;
    const matchesProvince = provinceFilter === "all" || site.province === provinceFilter;
    return matchesSearch && matchesStatus && matchesProvince;
  });

  return (
    <PlatformLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索站点名称或子域名..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
            >
              <option value="all">全部状态</option>
              <option value="active">运行中</option>
              <option value="stopped">已停止</option>
              <option value="expired">已到期</option>
            </select>
            <select
              value={provinceFilter}
              onChange={(e) => setProvinceFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
            >
              <option value="all">全部省份</option>
              {CHINA_PROVINCES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchSites}
            className="px-4 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-refresh-line" />
            刷新
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-800">{sites.length}</div>
            <div className="text-sm text-gray-500">站点总数</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-green-600">{sites.filter(s => s.status === "active").length}</div>
            <div className="text-sm text-gray-500">运行中</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-red-600">{sites.filter(s => s.status === "expired").length}</div>
            <div className="text-sm text-gray-500">已到期</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-blue-600">{sites.filter(s => s.status === "stopped").length}</div>
            <div className="text-sm text-gray-500">已停止</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-5 py-3 font-medium">站点</th>
                  <th className="px-5 py-3 font-medium">站点管理员</th>
                  <th className="px-5 py-3 font-medium">代理商</th>
                  <th className="px-5 py-3 font-medium">省份</th>
                  <th className="px-5 py-3 font-medium">模板</th>
                  <th className="px-5 py-3 font-medium">子域名</th>
                  <th className="px-5 py-3 font-medium">状态</th>
                  <th className="px-5 py-3 font-medium">创建时间</th>
                  <th className="px-5 py-3 font-medium">到期时间</th>
                  <th className="px-5 py-3 font-medium text-center">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-5 py-12 text-center text-gray-400">
                      <i className="ri-loader-2-line animate-spin text-2xl" />
                      <p className="mt-2">加载中...</p>
                    </td>
                  </tr>
                ) : filteredSites.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-5 py-12 text-center text-gray-400">
                      未找到站点
                    </td>
                  </tr>
                ) : (
                  filteredSites.map((site) => {
                    const status = statusMap[site.status] || { label: site.status, bg: "bg-gray-100", color: "text-gray-600" };
                    return (
                      <tr key={site.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600">
                              <i className="ri-global-line" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{site.name}</div>
                              {site.domains?.length > 0 && (
                                <div className="text-xs text-gray-400">
                                  {site.domains.slice(0, 2).join(', ')}
                                  {site.domains.length > 2 && `...`}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          {site.owner_name ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-lg bg-orange-50 text-orange-600">
                              {site.owner_name}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">未绑定</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          {site.agent || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500">
                          {site.province || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-50 text-purple-600">
                            {getTemplateName(site.template_id)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500">{site.subdomain}</td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                            {site.platform_disabled === 1 ? '平台已关闭' : status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500">{site.created_at?.split(' ')[0]}</td>
                        <td className="px-5 py-4 text-sm text-gray-500">{site.expire_at?.split(' ')[0]}</td>
                        <td className="px-5 py-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {site.status === 'active' ? (
                              <button
                                onClick={() => {
                                  if (!confirm(`确定要关闭站点「${site.name}」吗？关闭后代理商将无法自行开启。`)) return;
                                  updateSiteStatus(site.id, 'disable');
                                }}
                                className="px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                title="关闭站点"
                              >
                                <i className="ri-stop-circle-line mr-1" />关闭
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  if (!confirm(`确定要开启站点「${site.name}」吗？`)) return;
                                  updateSiteStatus(site.id, 'enable');
                                }}
                                className="px-2.5 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors cursor-pointer"
                                title="开启站点"
                              >
                                <i className="ri-play-circle-line mr-1" />开启
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {filteredSites.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <i className="ri-search-line text-3xl mb-3" />
              <p className="text-sm">未找到站点</p>
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
            <i className="ri-checkbox-circle-line text-green-400" />
            {toast}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
